import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Swal from "sweetalert2";
import axios from "axios";

import apiJanus from "../../services/apiJanus";

import location from "../../assets/Icons/location.svg";

import allBrazilStates from "../../helpers/allBrazilStates.json";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Spinner } from "../../components/Spinner";
import { BannerHome } from "../../components/Banners";

import { images_stores } from "./utils/images";
import {
  formatStorePhone,
  getWhatsAppUrl,
  isParaguayCountry,
} from "./utils/formatPhone";
import {
  displayStoreInfo,
  formatCityLabel,
  formatStoreAddress,
  hasStoreValue,
} from "./utils/formatStore";
import { removeAccentsAndLowerCase } from "../../utils/removeAccentsAndLowerCase";

import {
  BoxLeftContent,
  ButtonLocation,
  Container,
  ContentInfo,
  ContentStores,
  ContentStoresState,
  CountryBox,
  DefaultImage,
  SectionSearch,
  StoreBox,
  StoreBoxState,
  StoreImage,
  TopContent,
} from "./styles";

interface Store {
  id: number;
  company_name: string;
  state_registration: string;
  city: string;
  address: string;
  district: string;
  company_status: boolean;
  ifood_url: string;
  description_delivery_plataform: string;
  description_delivery_time: string;
  description_store_time: string;
  whatsapp_number: string;
  complement: string;
  number: string;
  telefone: string;
  cep: string;
  image: string;
  country?: string;
}

type City = {
  name: string;
  stores: {
    id: number;
    name: string;
  }[];
};

type State = {
  name: string;
  state_registration: string;
  citys: City[];
  country?: string;
};

type Country = {
  name: string;
  state: State[];
};

const toSlug = (value: string) =>
  removeAccentsAndLowerCase(value)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const PARAGUAY_STATE = {
  name: "Paraguai",
  state_registration: "PY",
};

const fetchStoresByCity = async (cityName: string): Promise<Store[]> => {
  const withoutSuffix = cityName.replace(/\s*\([^)]*\)\s*/g, " ").trim();
  const queries = [cityName, withoutSuffix].filter(
    (name, index, list) => name && list.indexOf(name) === index,
  );

  for (const city of queries) {
    const { data } = await apiJanus.get("/company/amabest-club", {
      params: { city },
    });

    if (data?.content?.length) {
      return data.content;
    }
  }

  return [];
};

export const Lojas = () => {
  const _location = useLocation();
  const navigate = useNavigate();
  const { state, city } = useParams();
  const { cityLocation, stateLocation, locationSearch } = _location.state || {};

  const [loading, setLoading] = useState(false);
  const [countrys, setCountrys] = useState<Country[]>([]);
  const [selectState, setSelectState] = useState<State | null>(null);
  const [selectCity, setSelectCity] = useState<string>("");
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const _stateHelmet = selectState
    ? removeAccentsAndLowerCase(
        selectState.state_registration || selectState.name,
      )
    : "";

  const _cityHelmet = selectCity ? toSlug(selectCity) : "";

  useEffect(() => {
    const fetchStatesAndStores = async () => {
      try {
        setLoading(true);
        const {
          data: { content },
        } = await apiJanus.get("/company/amabest-club");

        const _companiesActives = content.filter(
          (_company: any) => _company.company_status && _company.country,
        );

        let _countrys: Country[] = [];

        _companiesActives.forEach((_company: any) => {
          const paraguay = isParaguayCountry(_company.country);
          const brazilState = allBrazilStates.find(
            (_state) => _state.sigla === _company.state_registration,
          );

          if (!paraguay && !brazilState) {
            return;
          }

          const pushStore = (state: State) => {
            let cityItem = state.citys.find(
              (_city) => _city.name === _company.city,
            );

            if (!cityItem) {
              state.citys.push({
                name: _company.city,
                stores: [{ id: _company.id, name: _company.company_name }],
              });
              return;
            }

            if (!cityItem.stores.some((_store) => _store.id === _company.id)) {
              cityItem.stores.push({
                id: _company.id,
                name: _company.company_name,
              });
            }
          };

          if (paraguay) {
            let country = _countrys.find((_country) =>
              isParaguayCountry(_country.name),
            );

            if (!country) {
              country = {
                name: _company.country,
                state: [
                  {
                    name: PARAGUAY_STATE.name,
                    state_registration: PARAGUAY_STATE.state_registration,
                    country: _company.country,
                    citys: [],
                  },
                ],
              };
              _countrys.push(country);
            }

            pushStore(country.state[0]);
            return;
          }

          let country = _countrys.find(
            (_country) => _country.name === _company.country,
          );

          if (!country) {
            country = {
              name: _company.country,
              state: [
                {
                  name: brazilState?.nome || "",
                  state_registration: _company.state_registration,
                  country: _company.country,
                  citys: [],
                },
              ],
            };
            _countrys.push(country);
          }

          let state = country.state.find(
            (_state) =>
              _state.state_registration === _company.state_registration,
          );

          if (!state) {
            state = {
              name: brazilState?.nome || "",
              state_registration: _company.state_registration,
              country: _company.country,
              citys: [],
            };
            country.state.push(state);
          }

          pushStore(state);
        });

        setCountrys(_countrys);

        if (stateLocation) {
          const _stateFind =
            _countrys
              .flatMap((_country) => _country.state)
              .find(
                (_state) =>
                  removeAccentsAndLowerCase(_state.state_registration) ===
                  stateLocation,
              ) || null;

          if (_stateFind) {
            setSelectState(_stateFind);
          }
        }

        if (cityLocation) {
          setSelectCity(cityLocation);
        }

        if (locationSearch) {
          const _findLocationState =
            _countrys
              .flatMap((_country) => _country.state)
              .find(
                (_state) =>
                  removeAccentsAndLowerCase(_state.name) === locationSearch,
              ) || null;

          if (_findLocationState) {
            const _stateUrl = removeAccentsAndLowerCase(
              _findLocationState.state_registration || _findLocationState.name,
            );

            setSelectState(_findLocationState);
            setSelectCity("");
            navigate(`/lojas/${_stateUrl}`);
          } else {
            const _findLocationCityState =
              _countrys
                .flatMap((_country) => _country.state)
                .find((_state) =>
                  _state.citys.some(
                    (_city) =>
                      removeAccentsAndLowerCase(_city.name) === locationSearch,
                  ),
                ) || null;

            if (_findLocationCityState) {
              const _cityFind = _findLocationCityState.citys.find(
                (_city) =>
                  removeAccentsAndLowerCase(_city.name) === locationSearch,
              );

              if (_cityFind) {
                const _stateUrl = toSlug(
                  _findLocationCityState.state_registration ||
                    _findLocationCityState.name,
                );

                const _cityUrl = toSlug(_cityFind.name);

                setSelectState(_findLocationCityState);
                setSelectCity(_cityFind.name);
                navigate(`/lojas/${_stateUrl}/${_cityUrl}`);
              }
            }
          }
        }
      } catch {
        Swal.fire({
          title: "Ocorreu um erro!",
          text: "Parece que as cidades não estão disponíveis no momento.",
          timer: 4000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStatesAndStores();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!countrys.length) return;

    if (!state) {
      setSelectState(null);
      setSelectCity("");
      return;
    }

    const stateFind =
      countrys
        .flatMap((country) => country.state)
        .find((_state) => {
          const slugRegistration = toSlug(_state.state_registration);
          const slugName = toSlug(_state.name);
          const slugParam = toSlug(state);

          if (isParaguayCountry(_state.country) && isParaguayCountry(state)) {
            return true;
          }

          return slugRegistration === slugParam || slugName === slugParam;
        }) || null;

    if (!stateFind) {
      setSelectState(null);
      setSelectCity("");
      return;
    }

    setSelectState(stateFind);

    if (!city) {
      setSelectCity("");
      return;
    }

    const cityFind = stateFind.citys.find(
      (_city) => toSlug(_city.name) === toSlug(city),
    );

    setSelectCity(cityFind ? cityFind.name : "");
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrys, state, city]);

  useEffect(() => {
    const loadCityStores = async () => {
      if (!selectCity || !selectState) {
        setStores([]);
        return;
      }

      try {
        setLoadingStores(true);

        const apiStores = await fetchStoresByCity(selectCity);
        const cityStores = apiStores.filter(
          (store) =>
            store.company_status &&
            toSlug(store.city) === toSlug(selectCity),
        );

        setStores(
          cityStores.map((store) => ({
            ...store,
            country: store.country || selectState.country,
          })),
        );
      } catch {
        Swal.fire({
          icon: "error",
          title: "Erro ao carregar lojas",
          text: `Não foi possível carregar as lojas da cidade ${selectCity.toUpperCase()}.`,
        });
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    loadCityStores();
  }, [selectCity, selectState]);

  const getLocation = async () => {
    try {
      const { data } = await axios.get("https://ipapi.co/json");
      const { city, region_code } = data;

      if (city && region_code) {
        const _stateFind =
          countrys
            .flatMap((_country) => _country.state)
            .find(
              (_state) =>
                removeAccentsAndLowerCase(_state.state_registration) ===
                removeAccentsAndLowerCase(region_code),
            ) || null;

        if (_stateFind) {
          const _stateSelect = toSlug(
            _stateFind.state_registration || _stateFind.name,
          );

          const _citySelect = toSlug(city);

          setSelectState(_stateFind);
          setSelectCity(city);
          navigate(`/lojas/${_stateSelect}/${_citySelect}`);
        }
      }
    } catch {
      Swal.fire("Não foi possível obter sua localização.");
    }
  };

  return (
    <>
      <Helmet>
        <title>The Best Açaí - Nossas Lojas</title>
        <link
          rel="canonical"
          href={`https://thebestacai.com.br/lojas${
            _stateHelmet
              ? `/${_stateHelmet}${_cityHelmet ? `/${_cityHelmet}` : ""}`
              : ""
          }`}
        />
        <meta property="og:image" content={images_stores.banner_our_stores} />
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:image"
          content={images_stores.banner_our_stores_mobile}
        />
      </Helmet>
      <Navbar />
      <BannerHome
        img={images_stores.banner_our_stores}
        alt="Banner Nossas Lojas"
        text={false}
        imgMobile={images_stores.banner_our_stores_mobile}
      />
      <Container>
        {loading && !countrys.length ? (
          <Spinner />
        ) : (
          <>
            <TopContent>
              <SectionSearch>
                {!selectState ? (
                  <>
                    <h1>Nossas Lojas</h1>
                    <ButtonLocation onClick={getLocation}>
                      <img src={location} alt="Icone de Localização" />
                      Localizar loja mais próxima
                    </ButtonLocation>
                  </>
                ) : (
                  <>
                    <span className="selectsStateCity">
                      <span
                        className="span-hover"
                        onClick={() => {
                          setSelectCity("");
                          setSelectState(null);
                          navigate("/lojas");
                        }}
                      >
                        Nossas lojas
                      </span>{" "}
                      {" > "}{" "}
                      <span
                        className="span-hover"
                        onClick={() => {
                          const paraguay =
                            isParaguayCountry(selectState.country) ||
                            selectState.name === "Paraguai";

                          if (paraguay) {
                            setSelectCity("");
                            setSelectState(null);
                            navigate("/lojas");
                            return;
                          }

                          const _selectState = removeAccentsAndLowerCase(
                            selectState.state_registration || selectState.name,
                          );

                          setSelectCity("");
                          navigate(`/lojas/${_selectState}`);
                        }}
                      >
                        {selectState.name}
                      </span>
                      <span>
                        {" "}
                        {selectCity ? " > " + formatCityLabel(selectCity) : ""}
                      </span>
                    </span>
                  </>
                )}
              </SectionSearch>
            </TopContent>

            {!selectState && !selectCity ? (
              countrys
                .filter((_country) =>
                  _country.state.some(
                    (_state) =>
                      isParaguayCountry(_country.name) || _state.name,
                  ),
                )
                .map((_country) => (
                <CountryBox key={_country.name}>
                  <span className="title">{_country.name}</span>
                  <ContentStoresState>
                    {isParaguayCountry(_country.name)
                      ? (_country.state[0]?.citys || [])
                          .sort((_city1, _city2) =>
                            _city1.name.localeCompare(_city2.name),
                          )
                          .map((_city) => (
                            <StoreBoxState
                              key={_city.name}
                              onClick={() => {
                                const pyState = _country.state[0];
                                const _selectState = toSlug(
                                  pyState.state_registration || pyState.name,
                                );

                                setSelectState(pyState);
                                setSelectCity(_city.name);
                                navigate(
                                  `/lojas/${_selectState}/${toSlug(_city.name)}`,
                                );
                              }}
                            >
                              <h4>{formatCityLabel(_city.name)}</h4>
                              <p>{_city.stores.length} loja(s)</p>
                            </StoreBoxState>
                          ))
                      : _country.state
                          .filter((_state) => _state.name)
                          .sort((_state1, _state2) =>
                            _state1.name.localeCompare(_state2.name),
                          )
                          .map((_state) => (
                            <StoreBoxState
                              key={_state.state_registration}
                              onClick={() => {
                                const _selectState = removeAccentsAndLowerCase(
                                  _state.state_registration || _state.name,
                                );

                                setSelectState(_state);
                                setSelectCity("");
                                navigate(`/lojas/${_selectState}`);
                              }}
                            >
                              <h4>
                                {_state.name} - ({_state.state_registration})
                              </h4>
                              <p>
                                {_state.citys.reduce(
                                  (total, _city) =>
                                    total + _city.stores.length,
                                  0,
                                )}{" "}
                                loja(s)
                              </p>
                            </StoreBoxState>
                          ))}
                  </ContentStoresState>
                </CountryBox>
              ))
            ) : (
              <>
                {!selectCity ? (
                  <ContentStoresState>
                    {selectState?.citys
                      ?.sort((_city1, _city2) =>
                        _city1?.name?.localeCompare(_city2?.name),
                      )
                      ?.map((_city) => (
                        <StoreBoxState
                          key={_city.name}
                          onClick={() => {
                            const _selectState = toSlug(
                              selectState?.state_registration ||
                                selectState?.name ||
                                "",
                            );
                            const _selectCity = toSlug(_city.name);

                            setSelectCity(_city.name);
                            navigate(`/lojas/${_selectState}/${_selectCity}`);
                          }}
                        >
                          <h4>{formatCityLabel(_city.name)}</h4>
                          <p>{_city.stores.length} loja(s)</p>
                        </StoreBoxState>
                      ))}
                  </ContentStoresState>
                ) : loadingStores ? (
                  <Spinner />
                ) : (
                  <ContentStores>
                    {stores
                      .sort((_store1, _store2) =>
                        _store1?.address?.localeCompare(_store2?.address),
                      )
                      .map((store) => (
                          <StoreBox key={store.id}>
                            {!store?.image ? (
                              <DefaultImage
                                src={images_stores.banner_default_store}
                                alt="Imagem ilustrativa de uma loja"
                              />
                            ) : (
                              <StoreImage
                                src={store?.image}
                                alt="Imagem das lojas"
                              />
                            )}
                            <BoxLeftContent>
                              <h4>{formatStoreAddress(store)}</h4>
                              <ContentInfo>
                                <h6>
                                  ⏰ <strong>Horário:</strong>{" "}
                                  {displayStoreInfo(
                                    store.description_store_time,
                                  )}
                                </h6>
                                <h6>
                                  🛵 <strong>Delivery:</strong>{" "}
                                  {displayStoreInfo(
                                    store.description_delivery_time,
                                  )}
                                </h6>
                                <h6>
                                  📱 <strong>Plataformas:</strong>{" "}
                                  {displayStoreInfo(
                                    store.description_delivery_plataform,
                                  )}
                                </h6>
                                <h6>
                                  📞 <strong>Telefone:</strong>{" "}
                                  {hasStoreValue(store.telefone)
                                    ? formatStorePhone(store.telefone)
                                    : "Não informado"}
                                </h6>
                                {hasStoreValue(store.whatsapp_number) && (
                                  <h6>
                                    📞 <strong>Whatsapp:</strong>{" "}
                                    <a
                                      href={getWhatsAppUrl(
                                        store.whatsapp_number,
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {formatStorePhone(store.whatsapp_number)}
                                    </a>
                                  </h6>
                                )}
                              </ContentInfo>
                            </BoxLeftContent>
                          </StoreBox>
                      ))}
                  </ContentStores>
                )}
              </>
            )}
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};
